import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Alert, AlertType, GenericError, OtpInput } from '@egw/components'
import { Logger } from '@egw/logger'
import { WEALTH_ROUTES, EGM_A_COOKIE } from '@egw/constants'
import { useEffect, useState, type FC } from 'react'
import {
  useGetEnrollmentMultiReturnVerifyEmailData,
  useSubmitEnrollmentMultiResendReturnVerifyEmailData,
  useSubmitEnrollmentMultiReturnVerifyEmailData,
} from '@egw/api'
import { useNavigate } from 'react-router'
import { useTranslation } from '@egw/i18n'
import { CheckIcon } from '@heroicons/react/24/outline'
import { Cookies } from '@egw/clients'
import { enrollmentCookieService } from '@egw/enrollment-cookie'
import { PageHeader } from '../../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../../constants'

const log = new Logger('Enrollment - Multi - ReturnVerifyEmail')

export const ReturnVerifyEmailPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const [isInvalidToken, setIsInvalidToken] = useState(false)
  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)
  const [hasShownIntent, setHasShownIntent] = useState(false)
  const [otpValue, setOtpValue] = useState('')
  const [sessionNotFound, setSessionNotFound] = useState(false)
  const [isExpiredToken, setIsExpiredToken] = useState(false)

  const { data, isLoading, error } = useGetEnrollmentMultiReturnVerifyEmailData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiReturnVerifyEmailData()

  const {
    mutate: resend,
    isLoading: isResendSubmitting,
    error: submitResendError,
  } = useSubmitEnrollmentMultiResendReturnVerifyEmailData()

  const email = data?.email || ''

  // Set enrollment session cookie if it doesn't exist
  useEffect(() => {
    const existingCookie = Cookies.get(EGM_A_COOKIE)
    if (!existingCookie) {
      void enrollmentCookieService.resetSession()
    }
  }, [])

  useEffect(() => {
    if (data?.error === 'SESSION_NOT_FOUND') {
      void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.RETURN_SIGN_IN.path}`)
    }
  }, [data?.error])

  useEffect(() => {
    if (!hasShownIntent && otpValue) {
      setHasShownIntent(true)
    }
  }, [otpValue, hasShownIntent])

  const handleOtpComplete = (otp: string): void => {
    void onSubmit(otp)
  }

  const handleOtpChange = (value: string): void => {
    const trimmedValue = value.slice(0, 6)
    setOtpValue(trimmedValue)
  }

  const onSubmit = async (emailOtpCode: string): Promise<void> => {
    setIsInvalidToken(false)
    setIsExpiredToken(false)
    try {
      const response = await mutate({
        emailVerificationCode: emailOtpCode,
      })
      // Handle response
      if (response?.error === 'INVALID_VERIFICATION_CODE') {
        setIsInvalidToken(true)
      } else if (response?.error === 'VERIFICATION_CODE_EXPIRED') {
        setIsExpiredToken(true)
      } else if (response?.nextStep === 'SUBMIT_PHONE') {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.RETURN_ENTER_PHONE.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const resendEmail = (): void => {
    setIsInvalidToken(false)
    setCodeResent(true)

    setTimeout(() => {
      void sendMagicLinkEmail()
      setCounter(30)
      const intervalId = setInterval(() => {
        setCounter((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId)
            setCodeResent(false)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 5000)
  }

  const sendMagicLinkEmail = async (): Promise<void> => {
    setSessionNotFound(false)
    try {
      const response = await resend({
        triggerEmailOtp: true,
      })

      if (response?.error === 'SESSION_NOT_FOUND') {
        setSessionNotFound(true)
      } else if (response?.error === 'INVALID_VERIFICATION_CODE') {
        setIsInvalidToken(true)
      }
    } catch (e) {
      log.error('Resend email error', e)
    }
  }
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentMultiLayout title={t('multi.verify_email.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          <PageHeader title={t('multi.verify_email.title')} />
          <p className="typo-primary-base-regular text-text-body mb-3xs">{t('multi.verify_email.description')}</p>
          <p className="typo-primary-base-semibold text-text-header mb-md">{email}</p>
          <p className="typo-primary-base-regular text-text-meta mb-md">{t('multi.verify_email.check_your_email')}</p>
          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={handleOtpComplete}
            testId="otp-input"
            error={isInvalidToken || isExpiredToken ? t('multi.verify_email.code_expired') : ''}
          />

          <div className="flex items-baseline gap-4">
            <button
              onClick={resendEmail}
              disabled={isResendSubmitting || codeResent || isSubmitting}
              className={`typo-primary-base-semibold text-left mt-sm w-fit ${codeResent ? 'text-gray-400' : ' text-text-action'}`}
              type="button"
              data-testid="sendEmailOtp_button"
            >
              {t('multi.verify_email.resend_email')}
            </button>
            {codeResent && !counter ? (
              <p className="typo-primary-base-semibold text-text-header-success flex items-center gap-2">
                {t('multi.verify_email.code_sent')} <CheckIcon className="size-5" />
              </p>
            ) : null}
            {counter ? (
              <p className="typo-primary-base-semibold text-text-meta mt-2xs">
                {t('multi.verify_email.send_another_code', {
                  time: counter,
                })}
              </p>
            ) : null}
          </div>

          {sessionNotFound ? (
            <Alert alertType={AlertType.Error} description={t('multi.return.verify_email.session_not_found')} className="mt-sm" />
          ) : null}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? submitResendError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
